import { Typography, styled } from '@mui/material';
import {
  ChartsTooltip,
  ChartsTooltipCell,
  ChartsTooltipClasses,
  ChartsTooltipContainer,
  ChartsTooltipPaper,
  ChartsTooltipProps,
  ChartsTooltipRow,
  ChartsTooltipTable,
  chartsTooltipClasses,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { ChartsLabelMark } from '@mui/x-charts/ChartsLabel';
import React from 'react';

// values: the hovered point's visible numeric series values.
export type ChartTooltipFooter = (dataIndex: number, values: number[]) => { label: React.ReactNode; value: React.ReactNode } | null;

// Lets a chart pass the footer through slotProps.tooltip, which only accepts known tooltip props.
declare module '@mui/x-charts/models' {
  interface TooltipPropsOverrides {
    footer?: ChartTooltipFooter;
  }
}

interface FooterProps {
  // One row under the series for the hovered point, such as a total.
  footer?: ChartTooltipFooter;
}

type Props = ChartsTooltipProps & FooterProps;
type AxisFooterContentProps = Pick<ChartsTooltipProps<'axis'>, 'classes' | 'sort'> & Required<FooterProps>;

// The 9.15 cell declaration requires extra DOM props under React 18. Forward only the supported props used here.
const TooltipCell = styled(ChartsTooltipCell, {
  shouldForwardProp: (prop): prop is 'component' | 'className' | 'children' =>
    prop === 'component' || prop === 'className' || prop === 'children',
})({});

// Native axis content has no footer slot and renders its own paper, so this copies it and adds a tfoot.
const AxisFooterContent: React.FC<AxisFooterContentProps> = ({ footer, classes, sort }) => {
  const axesTooltip = useAxesTooltip();
  const className = (...slots: (keyof ChartsTooltipClasses)[]) =>
    slots
      .flatMap((slot) => [chartsTooltipClasses[slot], classes?.[slot]])
      .filter(Boolean)
      .join(' ');

  if (axesTooltip === null) {
    return null;
  }

  return (
    <ChartsTooltipPaper className={className('paper')}>
      {axesTooltip.map(({ axisId, mainAxis, axisValue, axisFormattedValue, dataIndex, seriesItems }) => {
        const visibleItems = seriesItems.filter((item) => typeof item.formattedValue === 'string');
        const sortedItems =
          sort && sort !== 'none'
            ? [...visibleItems].sort((a, b) => {
                const aValue = a.value?.valueOf();
                const bValue = b.value?.valueOf();
                if (typeof aValue !== 'number') {
                  return 1;
                }
                if (typeof bValue !== 'number') {
                  return -1;
                }
                return sort === 'asc' ? aValue - bValue : bValue - aValue;
              })
            : visibleItems;
        const numericValues = visibleItems.flatMap((item) => (typeof item.value === 'number' ? [item.value] : []));
        const footerRow = footer(dataIndex, numericValues);

        return (
          <ChartsTooltipTable key={axisId} className={className('table')}>
            {axisValue != null && !mainAxis.hideTooltip && <Typography component="caption">{axisFormattedValue}</Typography>}
            <tbody>
              {sortedItems.map((item) => (
                <ChartsTooltipRow key={item.seriesId} className={className('row')} data-series={item.seriesId} data-index={dataIndex}>
                  <TooltipCell component="th" className={className('labelCell', 'cell')}>
                    <div className={className('markContainer')}>
                      <ChartsLabelMark
                        type={item.markType}
                        markShape={item.markShape}
                        color={item.color}
                        className={className('mark')}
                      />
                    </div>
                    {item.formattedLabel || null}
                  </TooltipCell>
                  <TooltipCell component="td" className={className('valueCell', 'cell')}>
                    {item.formattedValue}
                  </TooltipCell>
                </ChartsTooltipRow>
              ))}
            </tbody>
            {footerRow && (
              <tfoot>
                <ChartsTooltipRow className={className('row')}>
                  <TooltipCell component="th" className={className('labelCell', 'cell')}>
                    {footerRow.label}
                  </TooltipCell>
                  <TooltipCell component="td" className={className('valueCell', 'cell')}>
                    {footerRow.value}
                  </TooltipCell>
                </ChartsTooltipRow>
              </tfoot>
            )}
          </ChartsTooltipTable>
        );
      })}
    </ChartsTooltipPaper>
  );
};

const ChartTooltip = ({ footer, ...props }: Props) => {
  if (props.trigger === 'none') {
    return null;
  }

  if (props.trigger === 'item' || !footer) {
    return <ChartsTooltip {...props} />;
  }

  const { sort, classes, ...containerProps } = props;

  return (
    <ChartsTooltipContainer {...containerProps} trigger="axis" classes={classes}>
      <AxisFooterContent footer={footer} classes={classes} sort={sort} />
    </ChartsTooltipContainer>
  );
};

export default ChartTooltip;
